/**
 * @fileoverview Guard global de autenticação pra rotas autenticadas.
 *
 * Responsabilidades:
 * 1. **Auto-logout reativo**: quando backend invalida sessão (401, 403
 *    account_expired) ou frontend chama `logout()`, `isAuthed` vira
 *    `false`. Sem este guard, telas montadas continuam renderizando e
 *    queries seguintes falham em loop. AuthGuard detecta e navega `/`.
 *
 * 2. **Auto-logout proativo (account_expired)**: se `accountExpiresAt`
 *    no store já passou (relógio do device atravessou meia-noite com
 *    user em tela cacheada), dispara `logout('account_expired')` ANTES
 *    de uma query falhar. Sem isso, user em tela com snapshot velho
 *    nunca vê a mensagem "conta expirou" — fica em loop de queries
 *    falhando. Check a cada 30s + 1× no mount.
 *
 * Render durante redirect: mantém `children` (último estado bom) em vez
 * de `null` — evita flash de tela em branco. RootRoute redireciona em
 * 1-2 frames; aí React desmonta o children naturalmente.
 */

import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { track } from '../qoe'
import { useSessionStore } from '../store'

const EXPIRY_CHECK_INTERVAL_MS = 30_000

export function AuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const isAuthed = useSessionStore((s) => s.isAuthed)
  const accountExpiresAt = useSessionStore((s) => s.accountExpiresAt)

  // Reativo: isAuthed virou false → navega `/`.
  useEffect(() => {
    if (!isAuthed) {
      navigate('/', { replace: true })
    }
  }, [isAuthed, navigate])

  // Proativo: relógio passou de accountExpiresAt → logout. Cobre TV
  // ligada virando o dia com plano vencido. Sem isso, user só vê o
  // erro quando a próxima query autenticada falhar (e mesmo assim só
  // se backend mapear corretamente — ver issue #24).
  useEffect(() => {
    if (!isAuthed || !accountExpiresAt) return
    const checkExpired = () => {
      const nowSec = Math.floor(Date.now() / 1000)
      if (nowSec >= accountExpiresAt) {
        track({
          type: 'auth_invalidated',
          ts: nowSec,
          reason: 'account_expired',
          source: 'client_expiry_check',
        })
        useSessionStore.getState().logout('account_expired')
      }
    }
    checkExpired()
    const timer = setInterval(checkExpired, EXPIRY_CHECK_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isAuthed, accountExpiresAt])

  return <>{children}</>
}
